/* ==========================================================================
 * MODKEI CRO — vanilla JS, no dependencies, no build step.
 * Custom elements:
 *   <modkei-countdown>  real campaign deadline, never resets
 *   <modkei-copy>       copy a discount code
 *   <modkei-bundle>     bundle selector + add to cart, then straight to /cart
 *   <modkei-sticky>     mobile sticky add-to-cart bar
 * Plus two global behaviours (see the bottom of the file):
 *   · añadir al carrito lleva directamente al carrito
 *   · “Pagar” va a /checkout sin depender del <form> del tema
 * ========================================================================== */
(function () {
  'use strict';

  // The file is loaded from the buy box, from the cart drawer and from the
  // trust bar, so on some pages it lands more than once. Running twice would
  // bind the global listeners twice and fire two navigations.
  if (window.__modkeiCro) return;
  window.__modkeiCro = true;

  /* ---------------------------------------------------------------- utils */

  var CURRENCY =
    (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR';

  function routeRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  var fmt;
  try {
    fmt = new Intl.NumberFormat(document.documentElement.lang || 'es-ES', {
      style: 'currency',
      currency: CURRENCY,
      minimumFractionDigits: 2,
    });
  } catch (e) {
    fmt = null;
  }

  /** @param {number} cents */
  function money(cents) {
    var v = (cents || 0) / 100;
    return fmt ? fmt.format(v) : v.toFixed(2) + ' ' + CURRENCY;
  }

  function pad(n) {
    return n < 10 ? '0' + n : String(n);
  }

  /**
   * Push a CRO event without touching Shopify's own analytics or the Meta pixel.
   * Existing pixel events keep firing untouched; these are additive only.
   */
  function track(name, payload) {
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: 'modkei_' + name }, payload || {}));
      document.dispatchEvent(
        new CustomEvent('modkei:' + name, { detail: payload || {}, bubbles: true })
      );
    } catch (e) {
      /* analytics must never break the page */
    }
  }

  /* ------------------------------------------------------- <modkei-countdown>
   * Counts down to a REAL campaign end date passed in `deadline` (ISO 8601).
   * When the campaign is over the whole promo card is removed from the DOM —
   * it never loops, never resets on refresh, never invents time.
   */

  class ModkeiCountdown extends HTMLElement {
    connectedCallback() {
      var raw = this.getAttribute('deadline');
      this.deadline = raw ? Date.parse(raw) : NaN;

      if (!raw || isNaN(this.deadline)) {
        this.#expire();
        return;
      }

      this.units = {
        hours: this.querySelector('[data-unit="hours"]'),
        minutes: this.querySelector('[data-unit="minutes"]'),
        seconds: this.querySelector('[data-unit="seconds"]'),
        days: this.querySelector('[data-unit="days"]'),
      };

      this.#tick();
      this.timer = setInterval(this.#tick, 1000);

      // Pause the interval while the tab is hidden, resync on return.
      this.onVisibility = () => {
        if (document.hidden) {
          clearInterval(this.timer);
        } else {
          this.#tick();
          this.timer = setInterval(this.#tick, 1000);
        }
      };
      document.addEventListener('visibilitychange', this.onVisibility);
    }

    disconnectedCallback() {
      clearInterval(this.timer);
      document.removeEventListener('visibilitychange', this.onVisibility);
    }

    #expire = () => {
      clearInterval(this.timer);
      var card = this.closest('.mk-promo');
      if (card) card.hidden = true;
      else this.hidden = true;
    };

    #tick = () => {
      var left = this.deadline - Date.now();
      if (left <= 0) return this.#expire();

      var total = Math.floor(left / 1000);
      var d = Math.floor(total / 86400);
      var h = Math.floor((total % 86400) / 3600);
      var m = Math.floor((total % 3600) / 60);
      var s = total % 60;

      // Roll days into hours when the campaign has under two days to run —
      // "39 h" reads as more urgent and is just as true as "1 d 15 h".
      if (this.units.days) {
        this.#set('days', d);
        this.#set('hours', h);
      } else {
        this.#set('hours', d * 24 + h);
      }
      this.#set('minutes', m);
      this.#set('seconds', s);
    };

    #set(unit, value) {
      var el = this.units[unit];
      if (el && el.textContent !== pad(value)) el.textContent = pad(value);
    }
  }

  /* ------------------------------------------------------------ <modkei-copy> */

  class ModkeiCopy extends HTMLElement {
    connectedCallback() {
      this.btn = this.querySelector('button');
      if (!this.btn) return;
      this.original = this.btn.textContent;
      this.btn.addEventListener('click', this.#copy);
    }

    disconnectedCallback() {
      if (this.btn) this.btn.removeEventListener('click', this.#copy);
      clearTimeout(this.reset);
    }

    #copy = async () => {
      var code = this.getAttribute('value') || '';
      try {
        await navigator.clipboard.writeText(code);
      } catch (e) {
        var ta = document.createElement('textarea');
        ta.value = code;
        ta.setAttribute('readonly', '');
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        try {
          document.execCommand('copy');
        } catch (err) {
          /* clipboard unavailable — the code is visible on screen anyway */
        }
        document.body.removeChild(ta);
      }

      this.btn.textContent = this.getAttribute('copied-label') || 'Copiado';
      this.btn.dataset.copied = 'true';
      track('promo_code_copied', { code: code });

      clearTimeout(this.reset);
      this.reset = setTimeout(() => {
        this.btn.textContent = this.original;
        delete this.btn.dataset.copied;
      }, 2200);
    };
  }

  /* ---------------------------------------------------------- <modkei-bundle>
   * Owns the buy box. Reads real variant IDs and real prices from data
   * attributes rendered by Liquid, so the arithmetic on screen always matches
   * what Shopify will charge. The strap discount is a real automatic discount
   * configured in the Shopify admin — this only mirrors it.
   */

  class ModkeiBundle extends HTMLElement {
    connectedCallback() {
      this.watchId = this.dataset.watchVariant;
      this.watchPrice = parseInt(this.dataset.watchPrice, 10) || 0;
      this.available = this.dataset.available === 'true';

      this.straps = {
        leather: {
          id: this.dataset.leatherVariant,
          price: parseInt(this.dataset.leatherPrice, 10) || 0,
        },
        rubber: {
          id: this.dataset.rubberVariant,
          price: parseInt(this.dataset.rubberPrice, 10) || 0,
        },
      };

      // Fraction of the strap price the customer actually pays (0.5 = 50% off).
      this.strapRate = parseFloat(this.dataset.strapRate || '0.5');

      this.atc = this.querySelector('[data-mk-atc]');
      this.atcPrice = this.querySelector('[data-mk-atc-price]');
      this.atcLabel = this.querySelector('[data-mk-atc-label]');

      this.addEventListener('change', this.#onChange);
      if (this.atc) this.atc.addEventListener('click', this.#onAdd);

      this.#render();
      track('product_view', {
        variant_id: this.watchId,
        price: this.watchPrice / 100,
        currency: CURRENCY,
      });
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.#onChange);
      if (this.atc) this.atc.removeEventListener('click', this.#onAdd);
    }

    get selection() {
      var opt = this.querySelector('input[name="mk-bundle"]:checked');
      return opt ? opt.value : 'watch';
    }

    get strapChoice() {
      var s = this.querySelector('input[name="mk-strap"]:checked');
      return s ? s.value : 'leather';
    }

    /** Line items in the exact shape /cart/add.js expects. */
    get lines() {
      var sel = this.selection;
      var items = [
        {
          id: this.watchId,
          quantity: 1,
          properties: { _bundle: sel },
        },
      ];

      if (sel === 'one') {
        var pick = this.straps[this.strapChoice];
        if (pick && pick.id) items.push({ id: pick.id, quantity: 1, properties: { _bundle: sel } });
      } else if (sel === 'both') {
        ['leather', 'rubber'].forEach((k) => {
          var s = this.straps[k];
          if (s && s.id) items.push({ id: s.id, quantity: 1, properties: { _bundle: sel } });
        });
      }
      return items;
    }

    get total() {
      var sel = this.selection;
      var t = this.watchPrice;
      if (sel === 'one') {
        t += Math.round(this.straps[this.strapChoice].price * this.strapRate);
      } else if (sel === 'both') {
        t +=
          Math.round(this.straps.leather.price * this.strapRate) +
          Math.round(this.straps.rubber.price * this.strapRate);
      }
      return t;
    }

    #onChange = (e) => {
      if (!e.target.name) return;
      if (e.target.name === 'mk-bundle') {
        track('bundle_selected', { bundle: this.selection, total: this.total / 100 });
      }
      if (e.target.name === 'mk-strap') {
        track('strap_selected', { strap: this.strapChoice });
      }
      this.#render();
    };

    #render() {
      var total = this.total;
      if (this.atcPrice) this.atcPrice.textContent = money(total);
      if (this.atcLabel) {
        this.atcLabel.textContent =
          this.selection === 'watch'
            ? this.getAttribute('label-single') || 'Añadir al carrito'
            : this.getAttribute('label-bundle') || 'Añadir el pack';
      }
      if (this.atc) this.atc.disabled = !this.available;

      // Shopify's express-checkout button only carries the watch variant, so it is
      // hidden whenever a bundle is selected — otherwise a shopper could tap Shop Pay
      // after choosing straps and silently lose them.
      var express = document.querySelectorAll(
        'shopify-accelerated-checkout, shopify-accelerated-checkout-cart, .shopify-payment-button'
      );
      for (var i = 0; i < express.length; i++) {
        express[i].hidden = this.selection !== 'watch';
      }

      // Keep the sticky bar in sync with the buy box.
      document.dispatchEvent(
        new CustomEvent('modkei:total-change', {
          detail: { total: total, formatted: money(total), bundle: this.selection },
        })
      );
    }

    #onAdd = async (e) => {
      e.preventDefault();
      if (!this.available || this.atc.getAttribute('aria-busy') === 'true') return;

      this.atc.setAttribute('aria-busy', 'true');
      var sel = this.selection;

      try {
        var res = await fetch(routeRoot() + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: this.lines }),
        });

        if (!res.ok) throw new Error('cart/add failed: ' + res.status);
        await res.json();

        track('add_to_cart', {
          bundle: sel,
          strap: sel === 'one' ? this.strapChoice : sel === 'both' ? 'both' : null,
          value: this.total / 100,
          currency: CURRENCY,
          items: this.lines.length,
        });
      } catch (err) {
        console.warn('[modkei] add to cart failed', err);
      }

      // Straight to the cart, whether the add succeeded or not. If it failed the
      // shopper sees the real cart contents instead of a button that did nothing.
      window.location.href = routeRoot() + 'cart';
    };
  }

  /* ---------------------------------------------------------- <modkei-sticky>
   * Appears only once the real add-to-cart button has scrolled out of view.
   */

  class ModkeiSticky extends HTMLElement {
    connectedCallback() {
      this.priceEl = this.querySelector('[data-mk-sticky-price]');
      this.btn = this.querySelector('[data-mk-sticky-btn]');
      this.anchor = document.querySelector('[data-mk-atc]');

      if (!this.anchor) {
        this.remove();
        return;
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          var visible = entries[0].isIntersecting;
          this.dataset.visible = visible ? 'false' : 'true';
        },
        { rootMargin: '0px 0px -10% 0px', threshold: 0 }
      );
      this.observer.observe(this.anchor);

      this.onTotal = (e) => {
        if (this.priceEl) this.priceEl.textContent = e.detail.formatted;
      };
      document.addEventListener('modkei:total-change', this.onTotal);

      if (this.btn) {
        this.btn.addEventListener('click', () => {
          track('sticky_atc_click', {});
          this.anchor.click();
        });
      }
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      document.removeEventListener('modkei:total-change', this.onTotal);
    }
  }

  /* ------------------------------------------------------- checkout hardening
   * “Pagar” is a submit button that sits OUTSIDE its own <form> and depends on
   * the form="cart-form" attribute to reach it. That association is fragile:
   * every time the theme re-renders the cart summary the button can end up
   * pointing at a form that no longer exists, and then it silently does
   * nothing — no error, no navigation, no clue for the shopper.
   *
   * Going to /checkout directly removes the dependency. Nothing is lost by
   * skipping the form POST: Horizon already saves the cart note and the
   * discount code over AJAX as they are typed, so the server-side cart is
   * always up to date by the time this runs.
   */

  function hardenCheckoutButtons() {
    document.addEventListener(
      'click',
      function (event) {
        var target = event.target;
        if (!target || typeof target.closest !== 'function') return;

        var btn = target.closest('#checkout, [name="checkout"], .cart__checkout-button');
        if (!btn || btn.disabled || btn.getAttribute('aria-busy') === 'true') return;

        event.preventDefault();
        btn.setAttribute('aria-busy', 'true');
        track('checkout_click', {});
        window.location.href = routeRoot() + 'checkout';
      },
      true
    );
  }

  /* ----------------------------------------------------------------- define */

  function define(name, ctor) {
    if (!customElements.get(name)) customElements.define(name, ctor);
  }

  define('modkei-countdown', ModkeiCountdown);
  define('modkei-copy', ModkeiCopy);
  define('modkei-bundle', ModkeiBundle);
  define('modkei-sticky', ModkeiSticky);

  hardenCheckoutButtons();
})();
